import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/base/MovieCard";
import MovieCardSkeleton from "@/components/base/MovieCardSkeleton";
import { movies, getHotMovies, type MovieType } from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";

const typeFilters: { key: "all" | MovieType; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
];

const hotPicks = getHotMovies(4);

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get("q") || "").trim();
  const [keyword, setKeyword] = useState(query);
  const [typeFilter, setTypeFilter] = useState<"all" | MovieType>("all");
  const [loading, setLoading] = useState(false);

  usePageMeta({
    title: query
      ? `“${query}”的搜索结果 - 嫩草影院`
      : "影视搜索 - 嫩草影院高清在线观看",
    description: query
      ? `嫩草影院站内搜索“${query}”的结果，可按电影或电视剧筛选，查看影片简介并进入详情页了解剧情。`
      : "在嫩草影院搜索电影、电视剧，按片名关键词快速查找影视内容，支持电影与剧集分类筛选。",
    keywords: `嫩草影院搜索,影视搜索,${query || "电影搜索"},在线观看`,
  });

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useEffect(() => {
    if (!query) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 420);
    return () => window.clearTimeout(timer);
  }, [query]);

  const results = useMemo(() => {
    if (!query) return [];
    const lower = query.toLowerCase();
    return movies.filter((movie) => {
      const matchKeyword =
        movie.title.toLowerCase().includes(lower) ||
        movie.englishTitle.toLowerCase().includes(lower) ||
        movie.genres.some((genre) => genre.includes(query)) ||
        movie.cast.some((actor) => actor.toLowerCase().includes(lower));
      const matchType = typeFilter === "all" || movie.type === typeFilter;
      return matchKeyword && matchType;
    });
  }, [query, typeFilter]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    setSearchParams(value ? { q: value } : {});
  };

  const countFor = (key: "all" | MovieType) => {
    if (!query) return 0;
    const lower = query.toLowerCase();
    return movies.filter((movie) => {
      const matchKeyword =
        movie.title.toLowerCase().includes(lower) ||
        movie.englishTitle.toLowerCase().includes(lower) ||
        movie.genres.some((genre) => genre.includes(query)) ||
        movie.cast.some((actor) => actor.toLowerCase().includes(lower));
      return matchKeyword && (key === "all" || movie.type === key);
    }).length;
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16 md:pt-[72px]">
        <section className="w-full bg-background-300 border-b border-background-400/60">
          <div className="w-full px-4 md:px-8 py-8 md:py-10">
            <nav className="flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-500 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800">站内搜索</span>
            </nav>
            <h1 className="mt-4 text-2xl md:text-3xl font-bold text-foreground-950">
              {query ? `“${query}”的搜索结果` : "搜索影视内容"}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              支持按片名、类型或主演关键词查找，结果与详情页内容保持一致
            </p>

            <form onSubmit={handleSubmit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500 text-base" />
                <input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  type="search"
                  name="q"
                  placeholder="输入片名、类型或演员，例如：深海回声"
                  className="w-full h-12 pl-11 pr-4 rounded-md bg-background-100 border border-background-400 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-500 transition-colors"
                />
              </div>
              <button
                type="submit"
                className="h-12 px-7 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-search-line" />
                搜索
              </button>
            </form>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          {query && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-foreground-600">
                {loading ? (
                  "正在搜索…"
                ) : (
                  <>
                    共找到 <span className="text-primary-500 font-semibold">{results.length}</span>{" "}
                    部与“{query}”相关的影视
                  </>
                )}
              </p>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-400/70 self-start">
                {typeFilters.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setTypeFilter(item.key)}
                    className={`px-4 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      typeFilter === item.key
                        ? "bg-primary-500 text-white"
                        : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {item.label}
                    <span className="ml-1 text-[10px] opacity-80">{countFor(item.key)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5">
            {!query ? (
              <div className="py-16 rounded-lg bg-background-100 border border-background-300/70 text-center">
                <span className="w-14 h-14 mx-auto rounded-full bg-background-200 border border-background-400 flex items-center justify-center text-foreground-500 text-2xl">
                  <i className="ri-search-line" />
                </span>
                <p className="mt-4 text-sm text-foreground-800">输入关键词开始搜索</p>
                <p className="mt-1.5 text-xs text-foreground-500">
                  试试搜索“科幻”、“悬疑”，或者直接输入片名
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {["深海回声", "悬疑", "科幻", "家庭"].map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="px-3 py-1.5 rounded-full bg-background-200 border border-background-400/70 text-xs text-foreground-700 hover:border-primary-400 hover:text-foreground-950 transition-colors"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>
            ) : loading ? (
              <MovieCardSkeleton count={5} />
            ) : results.length === 0 ? (
              <div className="rounded-lg bg-background-100 border border-background-300/70 p-6 md:p-8">
                <div className="text-center">
                  <span className="w-14 h-14 mx-auto rounded-full bg-background-200 border border-background-400 flex items-center justify-center text-foreground-500 text-2xl">
                    <i className="ri-emotion-sad-line" />
                  </span>
                  <p className="mt-4 text-sm text-foreground-800">
                    没有找到与“{query}”相关的影视
                  </p>
                  <p className="mt-1.5 text-xs text-foreground-500">
                    换个关键词试试，或者看看下面的热门影片
                  </p>
                </div>
                <div className="mt-7">
                  <h2 className="text-sm font-semibold text-foreground-950 mb-3">热门影片推荐</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                    {hotPicks.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
                {results.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} showDescription />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}